import toast from '@/modules/toast/services';
// eslint-disable-next-line
import router from '@/router';
import { modifiedProperties } from '@/utils';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import apiFactory from '../../api';

const INIT_EDITOR = async ({ dispatch, commit }, { id, relativeVersionId = null, entity }) => {
  commit('INIT_EDITOR', { id, relativeVersionId, entity });
  const entityData = await dispatch('FETCH', { id, relativeVersionId });
  if (entityData.lastDraftRelativeVersionId) {
    // Versionable entity
    router.push({
      params: {
        relativeVersionId: entityData.lastDraftRelativeVersionId,
      },
    });
    return;
  }
  commit('SET_ENTITY_DATA', entityData);
};

/**
 * Get the entity data
 *
 * @param state
 * @param {String} id The entity Id.
 * @param {String} relativeVersionId optional
 * @returns {Promise}
 */
const FETCH = async ({ state }, { id, relativeVersionId = null }) => {
  const api = apiFactory(state.entity);
  return api.fetch(id, relativeVersionId);
};

/**
 * Updates the given fields in the current channel draft.
 * @return {Promise}
 */
const UPDATE_FIELDS = async ({ commit }, fields) => {
  commit('UPDATE_DRAFT', fields);
};

/**
 * Update vee-validate form errors
 * @return {Promise}
 */
const UPDATE_FORM_ERRORS = async ({ commit, state }, errors) => {
  if (isEqual(state.errors, errors)) return;
  commit('SET_FORM_ERRORS', errors || []);
};

/**
 * Saves the entity.
 *
 * @param commit
 * @param state
 * @returns {Promise}
 */
const SAVE = async ({ commit, state, rootGetters }) => {
  const {
    id, versionId, entity, master, draft,
  } = state;
  // Find modified fields
  const modifiedFields = modifiedProperties(draft, master);
  const payload = pick(draft, modifiedFields);
  // Apply the context to the payload
  payload.context = rootGetters.context;
  // Now we call the backend api to persist the diff
  const api = apiFactory(entity);
  // Commit the results
  const entityData = await api.update(id, versionId, payload);
  if (!entityData) {
    return Promise.reject();
  }
  console.log('Saving entity response:', entityData);
  commit('SET_ENTITY_DATA', entityData);
  return true;
};

const PUBLISH = async ({ commit, getters, state }) => {
  if (!getters.isPublishable) {
    console.error('Entity is not publishable');
    return;
  }
  const {
    id, versionId, entity, master,
  } = state;

  if (!master.isDraft) {
    console.error('Entity does not have a draft');
    return;
  }
  // Now we call the backend api
  const api = apiFactory(entity);
  const entityData = await api.publish(id, versionId);
  commit('SET_ENTITY_DATA', entityData);
  toast.notify('message.entity.actions.publishSuccess');
  const { relativeVersionId } = entityData;
  if (relativeVersionId) {
    router.push({
      params: {
        relativeVersionId,
      },
    });
  }
};

const CLONE = async ({ commit, state }) => {
  try {
    const { id, versionId, entity } = state;
    const api = apiFactory(entity);
    const entityData = await api.clone(id, versionId);
    toast.notify('message.entity.actions.clonedSuccess');
    commit('SET_ENTITY_DATA', entityData);
    const { relativeVersionId } = entityData;
    if (relativeVersionId) {
      router.push({
        params: {
          relativeVersionId,
        },
      });
    }
    return true;
  } catch (e) {
    console.log('Error cloning entity', e);
    toast.error('message.entity.actions.clonedFail');
    return false;
  }
};

const UNPUBLISH = async ({ state, commit }) => {
  const {
    id, entity, master,
  } = state;

  if (!master.published && !master.entityPublished) {
    console.warn('Entity is not published.');
    return;
  }

  const api = apiFactory(entity);
  const entityData = await api.unpublish(id);
  commit('SET_ENTITY_DATA', entityData);
  toast.notify('message.entity.actions.unpublishSuccess');
};

const MERGE = ({ state, commit }, { targetId, currentUrlDecision }) => {
  const entityType = state.entity;
  router.push({ name: 'entity.editor', params: { id: targetId, entity: entityType } });
  toast.notify('message.entity.actions.mergeSuccess');
};

const DELETE = async ({ state, commit, rootGetters }) => {
  const entity = state.entity;
  const api = apiFactory(state.entity);
  try {
    await api.remove(state.id);
    commit('RESET_DRAFT');
    commit('RESET_MASTER');
    toast.notify('message.entity.actions.deletedSuccess');
    const entityConfig = rootGetters.searcher[entity];
    if (entityConfig) {
      router.push({ name: 'searcher', params: { entity } });
      return;
    }
    router.back();
  } catch (error) {
    toast.notify('message.content.tools.deletedFail');
    console.error(error);
  }
};

const RESET = ({ commit }) => {
  commit('RESET_DRAFT');
  commit('RESET_MASTER');
};

const TOGGLE_ADVANCED_EDITOR = ({ commit }) => {
  commit('TOGGLE_ADVANCED_EDITOR');
};

const ENABLE_ADVANCED_EDITOR = ({ commit }) => {
  commit('ENABLE_ADVANCED_EDITOR');
};

export default {
  INIT_EDITOR,
  FETCH,
  UPDATE_FIELDS,
  UPDATE_FORM_ERRORS,
  SAVE,
  DELETE,
  MERGE,
  PUBLISH,
  UNPUBLISH,
  RESET,
  CLONE,
  TOGGLE_ADVANCED_EDITOR,
  ENABLE_ADVANCED_EDITOR,
};
